<script lang="ts">
    import Head from "../header/Head.svelte";
    import Navbar from "../header/Navbar.svelte";
    import Footer from "../footer/Footer.svelte";
    import ScenarioList from "./scenarios/ScenarioList.svelte";
    import ProjectHeader from "./header/ProjectHeader.svelte";
    import { Button } from "sveltestrap";
    import { appManager } from "../AppManager";
    import { _ } from "svelte-i18n";
    import * as turf from "@turf/turf";
    import type { SnapshotCommitData } from "../api/data/SnapshotCommitData";
    import Menu from "./Menu.svelte";
    import RouteplannersList from "./reroutes/RouteplannersList.svelte";
    import type { ScenarioData } from "../api/projects/data/scenarios/ScenarioData";
    import type { ReRouteData } from "../api/projects/data/reroute/ReRouteData";
    import { ProjectDataExtensions } from "../api/projects/data/projects/ProjectDataExtensions";
    import type { ProjectAndScenariosData } from "../api/projects/data/projects/ProjectAndScenariosData";

    export let id: string;
    export let view: "scenarios" | "matrices" | "publish" = "scenarios";

    let scenarios: ScenarioData[] = [];
    let reroutes: ReRouteData[] = [];
    let projectData: ProjectAndScenariosData;
    let projectDescription: string = "";
    let projectName: string = "";
    let canDelete: boolean = true;

    $: if (typeof projectData !== "undefined") {
        canDelete = !ProjectDataExtensions.hasPublished(projectData);
    }

    async function onDeleteProject(): Promise<void> {
        var result =
            await appManager.ModalHooks.ProjectDeleteConfirmModal.open(
                projectData,
            );

        if (result) {
            const deleted = await appManager.api.projects.deleteProject({
                accessToken: await appManager.api.getAccessToken(),
                id: projectData.id,
            });
            if (deleted) {
                window.location.href = appManager.PublicUrl;
            }
        }
    }

    async function getProjectData() {
        projectData = await appManager.api.projects.getProject({
            accessToken: await appManager.api.getAccessToken(),
            id: id,
        });
        scenarios = projectData.scenarios;

        scenarios.sort((a, b) => {
            const aName = a.functionalName;
            const aNumber = Number(aName);
            const bName = b.functionalName;
            const bNumber = Number(bName);

            if (!isNaN(aNumber) && !isNaN(bNumber)) {
                if (aNumber < bNumber) return -1;
                return 1;
            }

            if (aName < bName) return -1;
            return 1;
        });
        projectDescription = projectData.description || "";
        projectName = projectData.name || "";

        reroutes = projectData.reRoutes ?? [];
    }

    async function onCopy(scenario: ScenarioData): Promise<void> {
        const newBranch = await appManager.api.edit.copyBranch({
            accessToken: await appManager.api.getAccessToken(),
            branchId: scenario.branchId,
        });

        let highest: number = 0;
        projectData.scenarios.forEach((s) => {
            const current = Number(s.functionalName);
            if (current <= highest) return;

            highest = current;
        });
        let newFunctionalName: string = `${highest + 1}`;

        let newName = `Copy of ${
            scenario.name ?? `Scenario ${scenario.functionalName}`
        }`;

        const newScenario = await appManager.api.projects.createScenario({
            accessToken: await appManager.api.getAccessToken(),
            data: {
                branchId: newBranch.id,
                functionalName: newFunctionalName,
                projectId: projectData.id,
                name: newName,
                description: scenario.description,
                tripScenarioId: scenario.tripScenarioId,
            },
        });

        await getProjectData();
    }

    /**
     * Creates a new scenario within the project with fresh OSM-data
     */
    async function onCreate() {
        const accessToken = await appManager.api.getAccessToken();

        const commits = await appManager.api.edit.getLatestSnapshotCommits({
            accessToken,
        });
        const latestPlanetCommit: SnapshotCommitData = commits.find(
            (x) => x.snapshot.name === "planet",
        );
        const newBranch = await appManager.api.edit.createBranch({
            accessToken,
            extentPolygon: turf.polygon(projectData.area.coordinates),
            snapshotCommitId: latestPlanetCommit.id,
        });

        let highest: number = 0;
        let tripScenarioId: string = undefined;
        projectData.scenarios.forEach((s) => {
            const current = Number(s.functionalName);
            if (current <= highest) return;

            highest = current;
            tripScenarioId = s.tripScenarioId;
        });
        await appManager.api.projects.createScenario({
            accessToken,
            data: {
                branchId: newBranch.id,
                functionalName: `${highest + 1}`,
                projectId: projectData.id,
                name: `Scenario ${scenarios.length}`,
                description: $_("impact.project.scenarioCreatedAt", {
                    values: { date: latestPlanetCommit.tags.diff_day },
                }),
                tripScenarioId: tripScenarioId,
            },
        });

        await getProjectData();
    }

    const onCreateReRoute = async () => {
        const accessToken = await appManager.api.getAccessToken();

        await appManager.api.projects.createReroute({
            accessToken: accessToken,
            data: {
                projectId: projectData.id,
                name: $_("impact.project.routeplanner.namenew"),
                scenarios: [scenarios[0].id, scenarios[1].id],
            },
        });

        await getProjectData();
    };

    async function onRemove(scenario: ScenarioData) {
        await appManager.api.projects.deleteScenario({
            accessToken: await appManager.api.getAccessToken(),
            id: scenario.id,
        });

        await getProjectData();
    }

    const onRemovedReroute = async () => {
        await getProjectData();
    };

    const onPublishReroute = async () => {
        canDelete = !ProjectDataExtensions.hasPublished(projectData);
    };

    const onScenariosUpdate = async () => {
        projectData = projectData;
    };
</script>

{#await getProjectData()}
    <div />
{/await}
<Head />

<Navbar isDark={false} />

<div class="bg-light">
    <ProjectHeader {projectData} />

    <main class="pb-8 pb-md-11 mt-md-n6">
        <div class="container-md minvh-50">
            <div class="row">
                <div class="col-12 col-md-3">
                    {#if typeof projectData !== "undefined"}
                        <Menu bind:active={view} />
                    {/if}
                </div>

                {#if view === "scenarios"}
                    <div class="col-9">
                        <ScenarioList
                            {scenarios}
                            project={projectData}
                            on:copy={async (e) =>
                                await onCopy(e.detail.scenario)}
                            on:remove={async (e) =>
                                await onRemove(e.detail.scenario)}
                            on:create={onCreate}
                        />
                    </div>
                {/if}
                {#if view === "publish"}
                    <div class="col-9">
                        <RouteplannersList
                            {reroutes}
                            project={projectData}
                            on:create={onCreateReRoute}
                            on:removed={onRemovedReroute}
                            on:publish={onPublishReroute}
                            on:scenarios={onScenariosUpdate}
                        />
                    </div>
                {/if}
            </div>
            <div class="row">
                <div class="ms-auto col-auto mt-3">
                    {#if !canDelete}
                        <span title={$_("impact.project.cannotDeleteProject")}>
                            <Button class="btn btn-danger disabled">
                                <span class="fe fe-trash-2"></span>
                                {$_("impact.project.delete")}
                            </Button>
                        </span>
                    {:else}
                        <Button
                            class="btn btn-danger"
                            on:click={onDeleteProject}
                        >
                            <span class="fe fe-trash-2"></span>
                            {$_("impact.project.delete")}
                        </Button>
                    {/if}
                </div>
            </div>
        </div>
    </main>
</div>

<Footer />

<style>
</style>
